import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import {
  RootState,
  StateFromRoot,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { AuthState, baseQueryWithReauth } from "@/modules/app/store"
import { OSINT } from "../interfaces"

const tagTypes = ["QueriesList", "Query"] as const
const reducerPath = "sherlokApi"

export const baseUrl = new URL(
  import.meta.env.REACT_APP_SHERLOK_TG_API ?? "",
  window.location.origin,
)
  .toString()
  .replace(/\/$/, "")

export const sherlokApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(
    fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState<StateFromRoot<AuthState>>).auth
          ?.token
        if (token) {
          headers.set("authorization", `Bearer ${token}`)
        }
        return headers
      },
    }),
  ),
  endpoints: (build) => ({
    createBulk: build.mutation<OSINT.CreateBulkRes, OSINT.CreateBulkReq>({
      query: ({ body, idempotencyKey }) => ({
        url: "/queries/bulk",
        method: "POST",
        body,
        headers: idempotencyKey
          ? { "Idempotency-Key": idempotencyKey }
          : undefined,
      }),
      invalidatesTags: ["QueriesList"],
    }),
    list: build.query<OSINT.ListRes, OSINT.ListReq>({
      query: ({ queryParams }) => ({
        url: "/queries",
        method: "GET",
        params: queryParams,
      }),
      providesTags: ["QueriesList"],
    }),
    get: build.query<OSINT.QueryDto, OSINT.GetReq>({
      query: ({ pathParams }) => ({
        url: `/queries/${pathParams.id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, { pathParams }) => [
        { type: "Query" as const, id: pathParams.id },
      ],
    }),
    action: build.mutation<{ ok: true }, OSINT.ActionReq>({
      query: ({ pathParams, body }) => ({
        url: `/queries/${pathParams.id}/actions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { pathParams }) => [
        { type: "Query" as const, id: pathParams.id },
        "QueriesList",
      ],
    }),
  }),
})
dynamicMiddlewaresInstance.addMiddleware(sherlokApi.middleware)
registerReducer(sherlokApi.reducerPath, sherlokApi.reducer)

export const {
  useCreateBulkMutation,
  useListQuery,
  useGetQuery,
  useActionMutation,
} = sherlokApi
